import { PDFDocument } from 'pdf-lib';

const PAGE_PRESETS = {
    a4: { width: 595.28, height: 841.89 },
    letter: { width: 612, height: 792 },
    legal: { width: 612, height: 1008 },
};

function mmToPt(mm) {
    return (mm * 72) / 25.4;
}

function getTargetSize(preset, customWidthMm, customHeightMm, landscape) {
    if (preset === 'custom') {
        const w = mmToPt(Number(customWidthMm));
        const h = mmToPt(Number(customHeightMm));
        if (!w || !h || w < 72 || h < 72) throw new Error('invalidSize');
        return { width: w, height: h };
    }

    const base = PAGE_PRESETS[preset];
    if (!base) throw new Error('invalidSize');

    return landscape
        ? { width: base.height, height: base.width }
        : { width: base.width, height: base.height };
}

async function loadPdf(file) {
    const bytes = await file.arrayBuffer();
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

/**
 * Resize every page in a PDF to a target page size.
 * @param {File} file
 * @param {{ preset: string, customWidthMm?: number, customHeightMm?: number, marginMm?: number, contentScale?: number }} options
 */
export async function resizePdf(file, options) {
    const srcPdf = await loadPdf(file);
    const newPdf = await PDFDocument.create();
    const pageCount = srcPdf.getPageCount();
    const marginPt = mmToPt(Math.max(0, options.marginMm ?? 0));
    const contentScale = Math.max(0.1, Math.min(2, (options.contentScale ?? 100) / 100));

    for (let i = 0; i < pageCount; i++) {
        const [embedded] = await newPdf.embedPdf(srcPdf, [i]);
        const { width: srcW, height: srcH } = embedded.scale(1);
        const landscape = options.preset !== 'custom' && srcW > srcH;
        const { width: targetW, height: targetH } = getTargetSize(
            options.preset,
            options.customWidthMm,
            options.customHeightMm,
            landscape,
        );

        const page = newPdf.addPage([targetW, targetH]);
        const contentW = Math.max(targetW - marginPt * 2, 1);
        const contentH = Math.max(targetH - marginPt * 2, 1);
        const fitScale = Math.min(contentW / srcW, contentH / srcH);
        const scale = fitScale * contentScale;
        const drawW = srcW * scale;
        const drawH = srcH * scale;
        const x = marginPt + (contentW - drawW) / 2;
        const y = marginPt + (contentH - drawH) / 2;

        page.drawPage(embedded, { x, y, width: drawW, height: drawH });
    }

    const output = await newPdf.save();
    return new Blob([output], { type: 'application/pdf' });
}
